/**
 * PageSkeleton - Full page skeleton loaders for lazy-loaded routes
 * ANNADATA AI Design System 2026
 * 
 * Provides contextual skeleton loading states that match page layouts
 */
import { GEOMetadata } from './GEOMetadata';
// aria-label: design-system-bypass 


interface PageSkeletonProps {
    /** Type of page layout to show */
    variant?: 'dashboard' | 'list' | 'detail' | 'form' | 'minimal';
    /** Show header skeleton */
    showHeader?: boolean;
    /** Additional class name */
    className?: string;
}

const shimmerClass = `
    relative overflow-hidden
    bg-gray-200 dark:bg-gray-700
    before:absolute before:inset-0
    before:-translate-x-full
    before:animate-[shimmer_1.5s_infinite]
    before:bg-gradient-to-r
    before:from-transparent
    before:via-white/30
    before:to-transparent
`;

/**
 * Header skeleton with back button, logo, and action areas
 */
function HeaderSkeleton() {
    return (
        <div aria-label="Page Container" className="bg-gradient-to-r from-emerald-600 to-green-700 px-4 pt-8 pb-4">
            <div className="flex items-center justify-between mb-3">
                <div className={`${shimmerClass} w-8 h-8 rounded-full bg-white/20`} />
                <div className={`${shimmerClass} w-24 h-6 rounded-lg bg-white/20`} />
                <div className={`${shimmerClass} w-8 h-8 rounded-full bg-white/20`} />
            </div>
            <div className="flex items-center gap-3">
                <div className={`${shimmerClass} w-10 h-10 rounded-full bg-white/20`} />
                <div className="flex-1">
                    <div className={`${shimmerClass} w-20 h-3 rounded bg-white/20 mb-2`} />
                    <div className={`${shimmerClass} w-32 h-5 rounded bg-white/20`} />
                </div>
            </div>
        </div>
    );
}

/**
 * Dashboard skeleton with stats, cards, and grid
 */
function DashboardSkeleton() {
    return (
        <div aria-label="Page Container" className="px-4 space-y-4 pt-4">
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map(i => (
                    <div key={i} className={`${shimmerClass} h-20 rounded-xl`} />
                ))}
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 overflow-hidden">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`${shimmerClass} w-20 h-24 rounded-xl flex-shrink-0`} />
                ))}
            </div>

            {/* Main Card */}
            <div className={`${shimmerClass} h-32 rounded-2xl`} />

            {/* Grid */}
            <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <div key={i} className={`${shimmerClass} h-16 rounded-xl`} />
                ))}
            </div>

            {/* Another Card */}
            <div className={`${shimmerClass} h-24 rounded-2xl`} />
        </div>
    );
}

/**
 * List skeleton with avatar + text rows
 */
function ListSkeleton() {
    return (
        <div aria-label="Page Container" className="px-4 space-y-3 pt-4">
            {/* Search bar */}
            <div className={`${shimmerClass} h-12 rounded-xl`} />

            {/* List items */}
            {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl">
                    <div className={`${shimmerClass} w-12 h-12 rounded-xl flex-shrink-0`} />
                    <div className="flex-1 space-y-2">
                        <div className={`${shimmerClass} h-4 rounded w-3/4`} />
                        <div className={`${shimmerClass} h-3 rounded w-1/2`} />
                    </div>
                    <div className={`${shimmerClass} w-6 h-6 rounded-full`} />
                </div>
            ))}
        </div>
    );
}

/**
 * Detail page skeleton with hero and content sections
 */
function DetailSkeleton() {
    return (
        <div aria-label="Page Container" className="px-4 space-y-4 pt-4">
            {/* Hero image */}
            <div className={`${shimmerClass} h-48 rounded-2xl`} />

            {/* Title area */}
            <div className="space-y-2">
                <div className={`${shimmerClass} h-6 rounded w-2/3`} />
                <div className={`${shimmerClass} h-4 rounded w-1/2`} />
            </div>

            {/* Stats row */}
            <div className="flex gap-4">
                {[1, 2, 3].map(i => (
                    <div key={i} className="flex-1 space-y-1">
                        <div className={`${shimmerClass} h-8 rounded`} />
                        <div className={`${shimmerClass} h-3 rounded w-2/3`} />
                    </div>
                ))}
            </div>

            {/* Content paragraphs */}
            <div className="space-y-2 pt-4">
                <div className={`${shimmerClass} h-4 rounded`} />
                <div className={`${shimmerClass} h-4 rounded w-5/6`} />
                <div className={`${shimmerClass} h-4 rounded w-4/5`} />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 pt-4">
                <div className={`${shimmerClass} h-12 rounded-xl flex-1`} />
                <div className={`${shimmerClass} h-12 rounded-xl flex-1`} />
            </div>
        </div>
    );
}

/**
 * Form skeleton with input fields
 */
function FormSkeleton() {
    return (
        <div aria-label="Page Container" className="px-4 space-y-4 pt-4">
            {/* Form fields */}
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-2">
                    <div className={`${shimmerClass} h-4 rounded w-1/4`} />
                    <div className={`${shimmerClass} h-12 rounded-xl`} />
                </div>
            ))}

            {/* Upload area */}
            <div className={`${shimmerClass} h-32 rounded-2xl`} />

            {/* Submit button */}
            <div className={`${shimmerClass} h-14 rounded-xl mt-6`} />
        </div>
    );
}

/**
 * Minimal skeleton - just a centered loading area
 */
function MinimalSkeleton() {
    return (
        <div aria-label="Page Container" className="flex items-center justify-center min-h-[400px]">
            <div className="text-center space-y-4">
                <div className={`${shimmerClass} w-16 h-16 rounded-full mx-auto`} />
                <div className={`${shimmerClass} h-4 rounded w-32 mx-auto`} />
                <div className={`${shimmerClass} h-3 rounded w-24 mx-auto`} />
            </div>
        </div>
    );
}

/**
 * PageSkeleton - Main export component
 */
export function PageSkeleton({
    variant = 'dashboard',
    showHeader = true,
    className = '',
}: PageSkeletonProps) {
    return (
        <div className={`min-h-screen bg-gradient-to-br from-emerald-50 to-green-100 dark:from-gray-900 dark:to-emerald-900/20 ${className}`}>
            <GEOMetadata
                pageTitle="Loading... | Annadata AI"
                description="Please wait while we prepare your premium smart farming experience."
                seoMarkers="title= name='description' og:title"
            />
            {showHeader && <HeaderSkeleton />}

            {variant === 'dashboard' && <DashboardSkeleton />}
            {variant === 'list' && <ListSkeleton />}
            {variant === 'detail' && <DetailSkeleton />}
            {variant === 'form' && <FormSkeleton />}
            {variant === 'minimal' && <MinimalSkeleton />}

            {/* Bottom nav placeholder */}
            <div className="fixed bottom-4 left-4 right-4">
                <div className={`${shimmerClass} h-16 rounded-3xl bg-white/50 dark:bg-gray-800/50`} />
            </div>
        </div>
    );
}

export default PageSkeleton;
